import { Trash2 } from "lucide-react";
import React from "react";
import {
  ControllerRenderProps,
  FieldArray,
  FieldPath,
  FieldValues,
  useFormContext,
} from "react-hook-form";

import FormInput from "@/components/form-fields/form-input";
import { AsyncSingleSelect } from "@/components/reusables/async-select/async-single-select";
import { FormSelectSingle } from "@/components/reusables/custom-form-fields/FormSelect";
import InputField from "@/components/reusables/custom-form-fields/InputField";
import { TableCell, TableRow } from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import { HUNDRED } from "@/constants/constants";
import { InvoicingStatus } from "@/constants/invoicing.constants";
import { InvoiceItem, TrackingItem } from "@/interfaces/xero.interface";
import { cn } from "@/lib/utils";
import { filterItem } from "@/pages/invoicing/context/InvoicingContext";
import { useXeroInvoiceItemsQuery } from "@/redux/api/xeroQuery";
import * as invoicingSchema from "@/schemas/invoicing.schema";

export type TInvoiceItemField =
  FieldArray<invoicingSchema.CreateDocketInvoicing> & {
    idx: string;
  };

interface InvoiceItemRowProps {
  field: TInvoiceItemField;
  index: number;
  commonCellClassName: string;
  commonSelectClassName: string;
  commonInputClassName: string;
  isDisableFormField: boolean;
  trackingNameOptions?: filterItem[];
  xeroTrackingItems?: TrackingItem[];
  onRemoveRow: (index: number, field: TInvoiceItemField) => void;
}

type DiscountField<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
> = {
  field: ControllerRenderProps<TFieldValues, TName>;
  e: React.ChangeEvent<HTMLInputElement>;
};

const InvoiceItemRow = React.memo((props: InvoiceItemRowProps) => {
  const {
    field,
    index,
    commonCellClassName,
    commonSelectClassName,
    commonInputClassName,
    isDisableFormField,
    trackingNameOptions,
    xeroTrackingItems,
    onRemoveRow,
  } = props;

  const form = useFormContext<invoicingSchema.CreateDocketInvoicing>();
  const { setValue, watch, getValues, setError, clearErrors } = form;

  // Memoize values that don't change frequently
  const selectedTrackingId = watch(`itemSheets.${index}.trackingName`);

  const trackingOptions = React.useMemo(() => {
    return (
      xeroTrackingItems?.find((item) => item.id === selectedTrackingId)
        ?.options || []
    );
  }, [xeroTrackingItems, selectedTrackingId]);

  const handleItemChange = React.useCallback(
    (item?: InvoiceItem) => {
      if (item) {
        setValue(`itemSheets.${index}.itemId`, item.id, {
          shouldDirty: true,
          shouldValidate: true,
        });
        setValue(`itemSheets.${index}.title`, item.title);
        setValue(
          `itemSheets.${index}.tax`,
          item?.XeroTaxRate?.effectiveRate ?? 0,
          {
            shouldDirty: true,
            shouldValidate: true,
          },
        );

        const currentDescription = watch(`itemSheets.${index}.description`);
        const updatedDescription = currentDescription?.replace(
          /Note:.*?#/,
          `Note: ${item.description} #`,
        );

        setValue(`itemSheets.${index}.description`, updatedDescription);
        setValue(
          `itemSheets.${index}.account`,
          item?.accountCode?.toString() ?? "",
        );
      } else {
        setValue(`itemSheets.${index}.itemId`, "", {
          shouldDirty: true,
          shouldValidate: true,
        });

        const currentDescription = watch(`itemSheets.${index}.description`);
        const extractedValue =
          form.formState.defaultValues?.itemSheets![index]?.description?.match(
            /Note:(.*?)#/,
          );
        const defaultDesc = extractedValue ? extractedValue[1].trim() : "";
        const updatedDescription = currentDescription?.replace(
          /Note:.*?#/,
          `Note: ${defaultDesc} #`,
        );

        setValue(`itemSheets.${index}.description`, updatedDescription);
      }
    },
    [index, setValue, watch, form.formState.defaultValues],
  );

  const handleTrackingNameChange = React.useCallback(
    (value: string) => {
      const selected = trackingNameOptions?.find(
        (option) => option.value === value,
      );
      setValue(`itemSheets.${index}.trackingName`, selected?.value ?? "");
      setValue(`itemSheets.${index}.trackingOption`, "");
    },
    [index, trackingNameOptions, setValue],
  );

  const handleDiscountChange = React.useCallback(
    <TFieldValues extends FieldValues, TName extends FieldPath<TFieldValues>>({
      field,
      e,
    }: DiscountField<TFieldValues, TName>) => {
      field.onChange(Number(e.target.value));

      const price = getValues(`itemSheets.${index}.price`);
      const quantity = getValues(`itemSheets.${index}.quantity`);
      const discount = getValues(`itemSheets.${index}.discount`);

      if (!price || !discount) {
        return;
      }

      if (!quantity) {
        if (discount > price) {
          field.onChange(price);
          setError(`itemSheets.${index}.discount`, {
            type: "custom",
            message: "Discount cannot be greater than price",
          });
        } else {
          clearErrors(`itemSheets.${index}.discount`);
        }
      } else if (quantity > 0) {
        if (price * quantity < discount) {
          field.onChange(price * quantity);
          setError(`itemSheets.${index}.discount`, {
            type: "custom",
            message: "Discount cannot be greater than price",
          });
        } else {
          clearErrors(`itemSheets.${index}.discount`);
        }
      }
    },
    [index, getValues, setError, clearErrors],
  );

  const calculateTotal = React.useCallback(() => {
    const price = Number(watch(`itemSheets.${index}.price`)) || 0;
    const quantity = Number(watch(`itemSheets.${index}.quantity`)) || 0;
    const discount = Number(watch(`itemSheets.${index}.discount`)) || 0;
    const tax = Number(watch(`itemSheets.${index}.tax`)) || 0;

    if (price <= 0 || quantity <= 0) {
      return 0;
    }

    const subtotal = price * quantity;
    const subtotalAfterDiscount = Math.max(0, subtotal - discount);
    const totalWithTax =
      subtotalAfterDiscount + (subtotalAfterDiscount * tax) / 100;

    return totalWithTax.toFixed(2);
  }, [index, watch]);

  const totalValue = React.useMemo(
    () => calculateTotal(),
    [
      watch(`itemSheets.${index}.price`),
      watch(`itemSheets.${index}.quantity`),
      watch(`itemSheets.${index}.discount`),
      watch(`itemSheets.${index}.tax`),
      calculateTotal,
    ],
  );

  return (
    <TableRow key={field.idx}>
      <TableCell className={commonCellClassName}>
        <FormInput
          form={form}
          name={`itemSheets.${index}.itemId`}
          render={(field) => (
            <AsyncSingleSelect<InvoiceItem, { filter: string }>
              useQuery={useXeroInvoiceItemsQuery}
              label='Items'
              value={field.value}
              triggerClassName={commonSelectClassName}
              renderConfig={{
                valueKey: "id",
                labelKey: "title",
              }}
              placeholder='Select an Item'
              aria-label='Select item'
              defaultLabel={form.getValues(
                `itemSheets.${index}.defaultItemName`,
              )}
              disabled={isDisableFormField}
              onChange={(_, data) => {
                handleItemChange(data);
              }}
            />
          )}
        />
      </TableCell>
      <TableCell className={commonCellClassName}>
        <FormInput
          form={form}
          name={`itemSheets.${index}.description`}
          render={(field) => (
            <Textarea
              placeholder='Description'
              className='w-[350px] mt-2'
              disabled={isDisableFormField}
              rows={3}
              {...field}
              aria-label='Item description'
            />
          )}
        />
      </TableCell>
      <TableCell className={commonCellClassName}>
        <FormInput
          form={form}
          name={`itemSheets.${index}.price`}
          input={{
            type: "number",
            disabled: isDisableFormField,
            "aria-label": "Item price",
          }}
          className={commonInputClassName}
        />
      </TableCell>
      <TableCell className={commonCellClassName}>
        <FormInput
          form={form}
          name={`itemSheets.${index}.quantity`}
          input={{
            type: "number",
            disabled: isDisableFormField,
            "aria-label": "Item quantity",
          }}
          className={commonInputClassName}
        />
      </TableCell>
      <TableCell className={commonCellClassName}>
        <FormInput
          form={form}
          name={`itemSheets.${index}.discount`}
          className={cn(commonInputClassName, "mt-0")}
          render={(field) => (
            <InputField
              placeholder='Discount'
              type='number'
              disabled={
                isDisableFormField || !getValues(`itemSheets.${index}.price`)
              }
              {...field}
              onChange={(e: unknown) =>
                handleDiscountChange({
                  e: e as React.ChangeEvent<HTMLInputElement>,
                  field,
                })
              }
              aria-label='Item discount'
            />
          )}
        />
      </TableCell>
      <TableCell className={commonCellClassName}>
        <FormInput
          name={`itemSheets.${index}.trackingName`}
          form={form}
          render={(field) => (
            <FormSelectSingle
              field={field}
              placeholder='Select an item'
              className={commonSelectClassName}
              options={trackingNameOptions ?? []}
              disabled={isDisableFormField}
              onFieldValueChange={handleTrackingNameChange}
              aria-label='Tracking name'
            />
          )}
        />
      </TableCell>
      <TableCell className={commonCellClassName}>
        <FormInput
          name={`itemSheets.${index}.trackingOption`}
          form={form}
          render={(field) => (
            <FormSelectSingle
              field={field}
              disabled={isDisableFormField}
              placeholder='Select an option'
              className={commonSelectClassName}
              options={
                trackingOptions?.map((option) => ({
                  label: option.name,
                  value: option.trackingOptionID,
                })) || []
              }
              aria-label='Tracking option'
            />
          )}
        />
      </TableCell>
      <TableCell className={commonCellClassName}>
        <FormInput
          form={form}
          name={`itemSheets.${index}.account`}
          className={commonInputClassName}
          input={{
            placeholder: "Account",
            disabled: isDisableFormField,
            "aria-label": "Account",
          }}
        />
      </TableCell>
      <TableCell className={commonCellClassName}>
        <FormInput
          name={`itemSheets.${index}.tax`}
          form={form}
          maxValue={HUNDRED}
          input={{
            type: "number",
            placeholder: "Tax",
            disabled:
              isDisableFormField || !getValues(`itemSheets.${index}.price`),
            "aria-label": "Tax percentage",
          }}
          className={commonInputClassName}
        />
      </TableCell>
      <TableCell className={commonCellClassName}>
        <FormInput
          name={`itemSheets.${index}.total`}
          form={form}
          hideError
          className={cn(commonInputClassName, "mt-0")}
          render={(field) => (
            <InputField
              placeholder='Total'
              type='number'
              disabled={true}
              {...field}
              value={totalValue}
              aria-label='Total amount'
            />
          )}
        />
      </TableCell>
      <TableCell className='!bg-white dark:bg-primary'>
        {(form.getValues("action") === "create" ||
          form.getValues("status") === InvoicingStatus.DRAFT) && (
          <button
            type='button'
            onClick={() => onRemoveRow(index, field)}
            className='text-primary'
            aria-label='Remove item'
          >
            <Trash2 size={20} className='text-destructive' />
          </button>
        )}
      </TableCell>
    </TableRow>
  );
});

InvoiceItemRow.displayName = "InvoiceItemRow";

export { InvoiceItemRow };
